import { Component, lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { Nut } from '../components/co-ban';

/**
 * Tách gói giao diện theo từng màn: mở màn nào mới tải mã của màn đó, thay vì
 * bắt mọi người tải cả 20 màn (hơn 22 nghìn dòng) ngay lần đầu vào trang.
 *
 * `taiMan(() => import('./screens/X'), 'ManX')` — màn đang xuất khẩu theo tên
 * (named export) nên cần chỉ rõ tên.
 */
export function taiMan<M extends Record<string, unknown>, K extends keyof M>(
  nap: () => Promise<M>,
  ten: K,
): ComponentType {
  return lazy(async () => {
    const mod = await nap();
    // Tải được rồi thì cho phép tự tải lại một lần nữa ở lần triển khai sau.
    try {
      sessionStorage.removeItem(KHOA_DA_TAI_LAI);
    } catch {
      // sessionStorage bị chặn — bỏ qua.
    }
    return { default: mod[ten] as ComponentType };
  });
}

const KHOA_DA_TAI_LAI = 'agads-da-tai-lai-do-goi-cu';

/**
 * Sau mỗi lần triển khai, tên file gói đổi theo mã băm. Tab mở từ trước còn nhớ
 * tên cũ → bấm sang màn mới sẽ tải hụt. Gặp lỗi đó thì tự tải lại trang ĐÚNG
 * MỘT LẦN (đánh dấu bằng sessionStorage để không lặp vô hạn); vẫn hỏng thì hiện
 * thông báo kèm nút tải lại.
 */
function laLoiTaiGoi(loi: unknown): boolean {
  const noiDung = String((loi as { message?: string } | null)?.message ?? loi);
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError/i.test(
    noiDung,
  );
}

class HopBatLoiTai extends Component<{ children: ReactNode }, { loi: unknown | null }> {
  state = { loi: null as unknown | null };

  static getDerivedStateFromError(loi: unknown) {
    return { loi };
  }

  componentDidCatch(loi: unknown) {
    if (!laLoiTaiGoi(loi)) return;
    try {
      if (!sessionStorage.getItem(KHOA_DA_TAI_LAI)) {
        sessionStorage.setItem(KHOA_DA_TAI_LAI, '1');
        window.location.reload();
      }
    } catch {
      // sessionStorage bị chặn — giữ nguyên thông báo bên dưới.
    }
  }

  render() {
    const { loi } = this.state;
    if (loi === null) return this.props.children;
    const loiGoi = laLoiTaiGoi(loi);
    return (
      <div className="ag-the" style={{ maxWidth: 520 }}>
        <h2 style={{ fontSize: 16 }}>
          {loiGoi ? 'Không tải được màn hình này' : 'Màn hình gặp lỗi'}
        </h2>
        <p className="ag-mo" style={{ marginTop: 6 }}>
          {loiGoi
            ? 'AGADS vừa được cập nhật hoặc mạng bị gián đoạn. Bấm tải lại trang để lấy bản mới nhất.'
            : 'Có lỗi khi vẽ màn hình này. Bấm tải lại trang; nếu vẫn lỗi, báo kèm tên màn hình cho quản trị.'}
        </p>
        <Nut chinh style={{ marginTop: 10 }} onClick={() => window.location.reload()}>
          Tải lại trang
        </Nut>
      </div>
    );
  }
}

/** Bọc quanh một màn lazy: có khung chờ và bắt lỗi tải gói. */
export function KhungTaiMan({ children }: { children: ReactNode }) {
  return (
    <HopBatLoiTai>
      <Suspense
        fallback={
          <div role="status" aria-live="polite" className="ag-mo" style={{ padding: 24 }}>
            Đang tải màn hình…
          </div>
        }
      >
        {children}
      </Suspense>
    </HopBatLoiTai>
  );
}
